import { useState, useEffect, useRef } from 'react';
import { Bell, Shield, Save, Smartphone, Mail, RefreshCw, Check, Palette, Clock, Camera, X, Eye, EyeOff, LogOut, Calendar, Copy, Trash2, BarChart2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext.jsx';
import { useTheme } from '../context/ThemeContext.jsx';
import { usePWAContext } from '../context/PWAContext.jsx';
import { useTimeFormat } from '../hooks/useTimeFormat.js';
import api from '../services/api.js';
import { IS_NATIVE } from '../utils/apiBase.js';
import toast from 'react-hot-toast';

import Avatar from '../components/shared/Avatar.jsx';
// v2.5.135 — SMS-A user phone management.
import UserSmsSection from '../components/UserSmsSection.jsx';
const SPORT_AVATARS = {
  hockey:       ['🏒','🥅','🏟','⛸','🪙','🧤','🏆','🌨','🦈','❄️'],
  'ball-hockey':['🏒','🏑','🧤','🏆','🔥','⚡','💪','🏅','🌡','🎽'],
  soccer:       ['⚽','🥅','👟','🏆','🌍','🦅','⚡','🌟','🎯','🏅'],
  baseball:     ['⚾','🏏','🧢','🥎','🏟','🌟','🦅','💪','🏆','🎯'],
  softball:     ['🥎','⚾','🏟','🧢','💪','🌟','🏆','🎯','🌸','⚡'],
  basketball:   ['🏀','🏟','👟','🌟','🔥','⚡','💪','🏆','🦅','🎯'],
  football:     ['🏈','🦅','💪','🏆','⚡','🔥','🌟','🧢','🎯','🏅'],
  volleyball:   ['🏐','🏖','🌊','☀️','💪','🌟','🏆','⚡','🎯','🏅'],
  tennis:       ['🎾','🏆','👟','🌟','⚡','💪','🎯','🌿','🏅','🌞'],
  badminton:    ['🏸','🌟','💪','⚡','🏆','🎯','🌸','🌞','🏅','🦋'],
  curling:      ['🥌','🧹','❄️','⛏','🏆','🌨','🧤','🌟','🎯','🏅'],
  lacrosse:     ['🥍','🏆','💪','⚡','🌟','🔥','🦅','🎯','🏅','⛩'],
  rugby:        ['🏉','💪','🔥','⚡','🌟','🏆','🦅','🎯','🏅','🌿'],
  cricket:      ['🏏','⚾','🌟','💪','🏆','🎯','☀️','🌿','🏅','⛩'],
  other:        ['🏅','🏆','⭐','💪','🔥','⚡','🌟','🎯','🦅','🎖'],
};

function getSportAvatars(sport) {
  return SPORT_AVATARS[sport] || SPORT_AVATARS.hockey;
}

export default function ProfilePage() {
  const { user, updateUser, logout } = useAuth();
  const { theme, setTheme }  = useTheme();
  const { push }             = usePWAContext();
  const { is24h, toggle: toggleTimeFormat, formatTime } = useTimeFormat();
  const fileInputRef = useRef(null);

  // Delete-account modal state. Two-step flow:
  //   1. Show modal with explicit warning + checkbox
  //   2. User types "DELETE" + (if password account) enters password
  //   3. POST DELETE /auth/me, then logout + redirect to login
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteAcknowledged, setDeleteAcknowledged] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [nameForm,  setNameForm]  = useState({ name: '' });
  const [savingName,  setSavingName]  = useState(false);
  const [nameSaved,   setNameSaved]   = useState(false);

  const [avatarUrl,        setAvatarUrl]        = useState(null);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [savingAvatar,     setSavingAvatar]      = useState(false);
  const [teamSport,        setTeamSport]         = useState('hockey');

  const [prefs,       setPrefs]       = useState({ email: true, push: true, reminders: true, sms: true });
  const [savingPrefs, setSavingPrefs] = useState(false);

  // v2.5.97 — Calendar subscription state. `tokens` is the list
  // returned by GET /api/me/calendar-tokens. `teams` from the user's
  // membership feeds the "Add subscription for ___" picker. Each
  // entry in tokens has token + teamId + teamName + lastAccessed
  // + accessCount; the UI uses lastAccessed/accessCount to give
  // the user confidence that their calendar app is actually
  // pulling the feed.
  const [calendarTokens, setCalendarTokens] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [userTeams, setUserTeams] = useState([]);
  const [creatingCalendarFor, setCreatingCalendarFor] = useState(null);
  const [revokingCalendar, setRevokingCalendar] = useState(null);
  const [copiedToken, setCopiedToken] = useState(null);
  useEffect(() => {
    setCalendarLoading(true);
    Promise.all([
      api.get('/me/calendar-tokens').catch(() => ({ data: { tokens: [] } })),
      api.get('/teams').catch(() => ({ data: { teams: [] } })),
    ]).then(([ctRes, tRes]) => {
      setCalendarTokens(ctRes.data?.tokens || []);
      setUserTeams(tRes.data?.teams || tRes.data || []);
      setCalendarLoading(false);
      // v2.5.147 — scroll-to-anchor support. SchedulePage links to
      // /profile#calendar so the user lands directly on the
      // subscription UI without scrolling past prefs, password,
      // SMS settings etc. We wait until AFTER loading completes
      // before scrolling — otherwise the page is still re-laying
      // out as cards finish rendering and the scroll target's
      // position changes mid-scroll.
      if (window.location.hash === '#calendar') {
        // Tiny setTimeout — give React one paint to mount the
        // <div id="calendar"> after the loading state flips.
        setTimeout(() => {
          const el = document.getElementById('calendar');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
      }
    });
  }, []);
  const createCalendarToken = async (teamId) => {
    setCreatingCalendarFor(teamId);
    try {
      await api.post('/me/calendar-tokens', { teamId });
      const r = await api.get('/me/calendar-tokens');
      setCalendarTokens(r.data?.tokens || []);
      toast.success('Calendar subscription created');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create subscription');
    }
    setCreatingCalendarFor(null);
  };
  const revokeCalendarToken = async (token) => {
    // v2.5.148 — confirm before destroying. Revoking immediately
    // breaks any calendar app subscription pointing at this token.
    // Coaches reported accidental clicks (the trash icon is small
    // on mobile + adjacent to Copy). One-tap confirm prevents an
    // accidental revoke from forcing a 4-step recovery (revoke →
    // wait → add → paste URL into calendar app).
    const ok = window.confirm(
      "Revoke this calendar subscription?\n\n" +
      "Any calendar app using this URL will stop receiving updates. " +
      "You can create a new subscription anytime."
    );
    if (!ok) return;

    setRevokingCalendar(token);
    try {
      await api.delete(`/me/calendar-tokens/${token}`);
      setCalendarTokens(prev => prev.filter(t => t.token !== token));
      toast.success('Subscription revoked');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to revoke');
    }
    setRevokingCalendar(null);
  };

  // v2.5.148 — one-click rotate. Confirms first (this WILL break
  // any calendar app currently using the old URL), then POSTs to
  // the regenerate endpoint, then updates the local list with the
  // new token. The UI is identical to the existing pattern — the
  // user just sees the URL change.
  const [regeneratingCalendar, setRegeneratingCalendar] = useState(null);
  const regenerateCalendarToken = async (oldToken) => {
    const ok = window.confirm(
      "Regenerate this calendar URL?\n\n" +
      "The current URL will stop working IMMEDIATELY. You'll need to " +
      "paste the new URL into your calendar app — most apps don't " +
      "auto-update subscription URLs.\n\n" +
      "Continue?"
    );
    if (!ok) return;

    setRegeneratingCalendar(oldToken);
    try {
      const { data } = await api.post(`/me/calendar-tokens/${oldToken}/regenerate`);
      // Swap the token in-place in our local list so the order
      // doesn't jump around. Other fields (teamName, accessCount,
      // lastAccessed) carry over visually — the access counters
      // reset server-side because this is technically a new row,
      // but the user doesn't care about those for a freshly-rotated
      // token.
      setCalendarTokens(prev => prev.map(t =>
        t.token === oldToken
          ? { ...t, token: data.token, accessCount: 0, lastAccessed: null }
          : t
      ));
      toast.success('New URL generated — paste it into your calendar app');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to regenerate');
    }
    setRegeneratingCalendar(null);
  };
  // The feed URL is what the user copies into Apple Calendar /
  // Google Calendar / Outlook.
  // 2026-07-07 FIX (coach report: "Last fetched: never" + Safari
  // "address is invalid"): this used to point at api.game-day.app —
  // a host that has NO route at the gateway (the API is served under
  // app.game-day.app/api, same as axios' baseURL and BACKEND_URL in
  // every email link). Every calendar URL ever issued was therefore
  // unreachable. Use the SAME ORIGIN the app is running on; the
  // native (Capacitor) build has no meaningful window origin, so it
  // hardcodes the public host.
  const calendarFeedUrl = (token, scheme = 'https') => {
    const host = (IS_NATIVE || typeof window === 'undefined' || !window.location?.host)
      ? 'app.game-day.app'
      : window.location.host;
    return `${scheme}://${host}/api/calendar/${token}.ics`;
  };
  const copyFeedUrl = async (token) => {
    const url = calendarFeedUrl(token, 'https');
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2000);
    } catch {
      // Fallback: select the text in a hidden input
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2000);
    }
  };

  const [pwdForm,   setPwdForm]   = useState({ current: '', newPwd: '', confirm: '' });
  const [showPwds,  setShowPwds]  = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  // Apr 2026 (Tom_Notes H1) — email-change flow state.
  // We keep three separate concerns:
  //   • emailDraft       — the value in the input field
  //   • savingEmail      — request-in-flight flag for disable
  //   • emailRequestSent — true after a successful POST so the UI
  //                         flips to "check your inbox"
  const [emailDraft,       setEmailDraft]       = useState('');
  const [savingEmail,      setSavingEmail]      = useState(false);
  const [emailRequestSent, setEmailRequestSent] = useState(false);

  const [resending,  setResending]  = useState(false);
  const [resentDone, setResentDone] = useState(false);

  const [myStats,       setMyStats]       = useState(null);
  const [achievements,  setAchievements]  = useState(null);

  // May 4 2026 (v2.5.70) — Leave Team flow lifted from TeamPage's
  // Danger sub-page. Coaches still leave teams from Team Settings →
  // Account; for non-coach players the Team Settings nav entry is
  // hidden entirely now, so the only way to leave a team they're on
  // had to move somewhere a player can reach. Profile is the obvious
  // home — they already see their team list here. `leaveTarget` is
  // the team being confirmed for departure (null = closed); `leaving`
  // tracks the in-flight POST so we can disable the buttons.
  const [leaveTarget, setLeaveTarget] = useState(null); // {id, name} | null
  const [leaving,     setLeaving]     = useState(false);

  // Apr 2026 (Tom_Notes H2) — phone number form. Stored encrypted
  // server-side; on this page we just hold the user-typed string.
  // Backend strips/validates, returns the same shape via /auth/me.
  const [phoneForm,    setPhoneForm]    = useState({ phone: '' });
  const [savingPhone,  setSavingPhone]  = useState(false);
  const [phoneSaved,   setPhoneSaved]   = useState(false);

  // Credits moved to the Activity page (Apr 2026). No state needed here.

  useEffect(() => {
    if (user) {
      setNameForm({ name: user.name || '' });
      setPhoneForm({ phone: user.phone || '' });
      // Merge with defaults (all true) rather than replace — if the server
      // returns partial prefs (e.g. { email: true } with no push key), we
      // want push to stay on by default, not become undefined/falsy.
      if (user.notificationPrefs) {
        setPrefs({
          email:     user.notificationPrefs.email     !== false,
          push:      user.notificationPrefs.push      !== false,
          reminders: user.notificationPrefs.reminders !== false,
          // v2.5.135 — SMS pref. Defaults to TRUE if absent from
          // server response, matching the email/push pattern.
          sms:       user.notificationPrefs.sms       !== false,
        });
      }
      setAvatarUrl(user.avatarUrl || null);
    }
    api.get('/auth/me/stats').then(r => setMyStats(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: teams } = await api.get('/teams');
        if (teams?.[0]) {
          const { data } = await api.get(`/teams/${teams[0].id}/stats/achievements`);
          setAchievements(data);
          if (teams[0].settings?.team_sport) setTeamSport(teams[0].settings.team_sport);
        }
      } catch {}
    };
    load();
  }, []);

  // Credits load/refund helpers moved to ActivityPage (Apr 2026).

  const saveName = async (e) => {
    e.preventDefault();
    if (!nameForm.name.trim()) return toast.error('Name is required');
    setSavingName(true);
    try {
      await api.put('/auth/profile', { name: nameForm.name.trim() });
      updateUser({ name: nameForm.name.trim() });
      setNameSaved(true); setTimeout(() => setNameSaved(false), 2000);
      toast.success('Name updated!');
    } catch { toast.error('Failed to update name'); }
    setSavingName(false);
  };

  const saveAvatar = async (url) => {
    setSavingAvatar(true);
    try {
      await api.put('/auth/profile', { avatarUrl: url });
      setAvatarUrl(url);
      updateUser({ avatarUrl: url });
      setShowAvatarPicker(false);
      toast.success(url ? 'Avatar updated!' : 'Avatar removed');
    } catch { toast.error('Failed to update avatar'); }
    setSavingAvatar(false);
  };

  // Apr 2026 (Tom_Notes H2) — save phone. Sends raw user-typed
  // value; backend cleans/validates/encrypts. Empty string clears.
  const savePhone = async (e) => {
    e.preventDefault();
    setSavingPhone(true);
    try {
      const phone = phoneForm.phone.trim();
      await api.put('/auth/profile', { phone });
      updateUser({ phone });
      setPhoneSaved(true);
      setTimeout(() => setPhoneSaved(false), 2000);
      toast.success(phone ? 'Phone saved!' : 'Phone cleared');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save phone');
    }
    setSavingPhone(false);
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Photo must be under 2 MB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => saveAvatar(ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const savePrefs = async () => {
    setSavingPrefs(true);
    try {
      await api.put('/auth/profile', { notificationPrefs: prefs });
      updateUser({ notificationPrefs: prefs });
      toast.success('Preferences saved!');
    } catch { toast.error('Failed to save preferences'); }
    setSavingPrefs(false);
  };

  const changePwd = async (e) => {
    e.preventDefault();
    if (pwdForm.newPwd !== pwdForm.confirm) return toast.error('Passwords do not match');
    // Password rule — keep in sync with backend /auth/change-password and
    // the register/reset flows: 10–72 chars + upper + lower + number + special.
    if (pwdForm.newPwd.length < 10) return toast.error('Password must be at least 10 characters');
    if (pwdForm.newPwd.length > 72) return toast.error('Password must be 72 characters or fewer');
    if (!/[A-Z]/.test(pwdForm.newPwd)) return toast.error('Password must contain at least one uppercase letter');
    if (!/[a-z]/.test(pwdForm.newPwd)) return toast.error('Password must contain at least one lowercase letter');
    if (!/[0-9]/.test(pwdForm.newPwd)) return toast.error('Password must contain at least one number');
    if (!/[^A-Za-z0-9]/.test(pwdForm.newPwd)) return toast.error('Password must contain at least one special character (e.g. !@#$%)');
    // UX (matches backend Bug 3 fix): refuse same password client-side too,
    // so user gets immediate feedback instead of round-tripping to a 400.
    if (pwdForm.current === pwdForm.newPwd) return toast.error('New password must be different from your current password');
    setSavingPwd(true);
    try {
      await api.post('/auth/change-password', { currentPassword: pwdForm.current, newPassword: pwdForm.newPwd });
      toast.success('Password changed! Other devices have been signed out.');
      setPwdForm({ current: '', newPwd: '', confirm: '' });
      setShowPwds(false);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to change password'); }
    setSavingPwd(false);
  };

  // Apr 2026 (Tom_Notes H1) — request an email change. Sends a
  // verification link to the NEW address; user clicks it to complete
  // the swap. Old address gets a security notice in parallel.
  const requestEmailChange = async (e) => {
    e.preventDefault();
    const newEmail = emailDraft.trim().toLowerCase();
    if (!newEmail) return toast.error('Enter a new email address');
    // Light client-side validation. Backend revalidates.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      return toast.error('That doesn\'t look like a valid email');
    }
    if (newEmail === (user?.email || '').toLowerCase()) {
      return toast.error('That\'s already your current email');
    }
    setSavingEmail(true);
    try {
      await api.post('/auth/email-change', { newEmail });
      setEmailRequestSent(true);
      toast.success('Check your new email for the confirmation link');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not start email change');
    }
    setSavingEmail(false);
  };

  const cancelEmailChange = async () => {
    setSavingEmail(true);
    try {
      await api.delete('/auth/email-change');
      setEmailRequestSent(false);
      setEmailDraft('');
      toast.success('Email change cancelled');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not cancel');
    }
    setSavingEmail(false);
  };

  const enablePush  = async () => {
    const result = await push.subscribe();
    if (result?.reason === 'no_vapid_key') toast.error('Push not configured — add VITE_VAPID_PUBLIC_KEY');
    else if (result?.reason === 'denied')  toast.error('Push blocked — enable in browser site settings');
    else if (result?.success) {
      toast.success('Push notifications enabled! 🔔');
      // Clear server-side dismissed flag so prompt won't be suppressed if they disable again later
      try {
        const updated = { ...(user?.notificationPrefs || {}), pushDismissed: false };
        await api.put('/auth/profile', { notificationPrefs: updated });
        updateUser({ notificationPrefs: updated });
      } catch {}
    }
  };
  const disablePush = async () => { await push.unsubscribe(); toast.success('Push notifications disabled'); };

  const resendVerification = async () => {
    setResending(true);
    try {
      await api.post('/auth/resend-verification');
      setResentDone(true); toast.success('Verification email sent!');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to resend'); }
    setResending(false);
  };

  const sportAvatars = getSportAvatars(teamSport);

  return (
    <div className="animate-fade-in max-w-2xl">
      <h1 className="section-title text-4xl mb-8">Profile & Settings</h1>

      {/* ── Avatar + Identity ── */}
      <div className="card mb-5">
        <div className="flex items-center gap-4 mb-5">
          {/* Avatar with camera button */}
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <Avatar avatarUrl={avatarUrl} name={user?.name} size={64} />
            <button onClick={() => setShowAvatarPicker(v => !v)} title="Change avatar" style={{
              position: 'absolute', bottom: -2, right: -2,
              width: 22, height: 22, borderRadius: '50%',
              background: 'var(--ice)', border: '2px solid var(--bg)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#000',
            }}>
              <Camera size={11} />
            </button>
          </div>

          <div className="flex-1 min-w-0">
            <div className="font-bold text-xl">{user?.name}</div>
            <div className="text-secondary text-sm truncate">{user?.email}</div>
            <div className="flex items-center gap-2 mt-1">
              <span className="badge capitalize" style={{ background: 'var(--ice-a10)', color: 'var(--ice)' }}>{user?.role}</span>
              {user?.isVerified
                ? <span className="badge" style={{ background: 'rgba(0,255,136,0.1)', color: 'var(--goal)' }}><Check size={10} /> verified</span>
                : <span className="badge" style={{ background: 'rgba(255,149,0,0.1)', color: 'var(--puck)' }}>⚠ unverified</span>}
            </div>
          </div>
        </div>

        {/* Avatar picker panel */}
        {showAvatarPicker && (
          <div className="mb-5 p-4 rounded-xl" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-bold">Choose your avatar</p>
              <button onClick={() => setShowAvatarPicker(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-2)', padding: 2 }}>
                <X size={15} />
              </button>
            </div>

            <p className="text-xs text-secondary mb-2 uppercase tracking-wide font-semibold">Sport Avatars</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {sportAvatars.map((emoji, i) => (
                <button key={i} onClick={() => saveAvatar(emoji)} disabled={savingAvatar} style={{
                  width: 46, height: 46, borderRadius: 10, fontSize: 24,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', transition: 'all 0.15s',
                  background: avatarUrl === emoji ? 'var(--ice-a20)' : 'var(--surface-3)',
                  border: `2px solid ${avatarUrl === emoji ? 'var(--ice)' : 'var(--border)'}`,
                  transform: avatarUrl === emoji ? 'scale(1.1)' : 'scale(1)',
                }}>
                  {emoji}
                </button>
              ))}
            </div>

            <p className="text-xs text-secondary mb-2 uppercase tracking-wide font-semibold">Upload Photo</p>
            <div className="flex items-center gap-3 flex-wrap">
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp"
                style={{ display: 'none' }} onChange={handlePhotoUpload} />
              <button onClick={() => fileInputRef.current?.click()} disabled={savingAvatar}
                className="btn-ghost text-sm border border-white/15 flex items-center gap-2">
                <Camera size={14} />
                {savingAvatar ? 'Saving…' : 'Choose Photo'}
              </button>
              <span className="text-xs text-secondary">JPG, PNG, WebP · max 2 MB</span>
            </div>

            {avatarUrl && (
              <button onClick={() => saveAvatar(null)} disabled={savingAvatar}
                className="mt-3 text-xs text-secondary"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                Remove avatar
              </button>
            )}
          </div>
        )}

        {/* Unverified banner */}
        {!user?.isVerified && (
          <div className="p-3 rounded-xl flex items-center justify-between gap-3 mb-5"
            style={{ background: 'rgba(255,149,0,0.08)', border: '1px solid rgba(255,149,0,0.2)' }}>
            <div>
              <p className="text-sm font-medium" style={{ color: 'var(--puck)' }}>Email not verified</p>
              <p className="text-xs text-secondary mt-0.5">Check your inbox, or resend the verification link.</p>
            </div>
            <button onClick={resendVerification} disabled={resending || resentDone}
              className="btn-ghost border border-white/15 text-xs flex-shrink-0"
              style={{ color: resentDone ? 'var(--goal)' : 'var(--puck)' }}>
              {resending ? <div className="spinner" style={{ width: 12, height: 12 }} />
                : resentDone ? <><Check size={12} /> Sent!</>
                : <><RefreshCw size={12} /> Resend</>}
            </button>
          </div>
        )}

        {/* Edit name */}
        <form onSubmit={saveName}>
          <label className="label">Display Name</label>
          <div className="flex gap-2">
            <input type="text" className="input-field flex-1" value={nameForm.name}
              onChange={e => setNameForm({ name: e.target.value })}
              placeholder="Your name" autoComplete="name" />
            <button type="submit" disabled={savingName || nameForm.name === user?.name}
              className="btn-ice px-4 flex-shrink-0"
              style={nameSaved ? { background: 'rgba(0,255,136,0.2)', color: 'var(--goal)' } : {}}>
              {savingName ? <div className="spinner" style={{ width: 14, height: 14 }} />
                : nameSaved ? <Check size={15} /> : <Save size={15} />}
            </button>
          </div>
        </form>

        {/* Apr 2026 (Tom_Notes H2) — phone number.
            Optional field. Backend stores it encrypted at rest;
            once SMS plumbing is wired up (Twilio/SNS path) this is
            the address used for reminders, jab notifications, and
            spare-invite blasts. Coaches can also see it in the
            Players page contact list (already implemented for
            existing phone fields elsewhere in the app). */}
        <form onSubmit={savePhone} className="mt-3">
          <label className="label">Phone Number <span className="text-xs text-secondary" style={{ fontWeight: 400 }}>(optional, used for SMS reminders)</span></label>
          <div className="flex gap-2">
            <input type="tel" className="input-field flex-1" value={phoneForm.phone}
              onChange={e => setPhoneForm({ phone: e.target.value })}
              placeholder="+1 555 123 4567"
              autoComplete="tel" maxLength={30} />
            <button type="submit"
              disabled={savingPhone || phoneForm.phone === (user?.phone || '')}
              className="btn-ice px-4 flex-shrink-0"
              style={phoneSaved ? { background: 'rgba(0,255,136,0.2)', color: 'var(--goal)' } : {}}>
              {savingPhone ? <div className="spinner" style={{ width: 14, height: 14 }} />
                : phoneSaved ? <Check size={15} /> : <Save size={15} />}
            </button>
          </div>
          <p className="text-xs text-secondary mt-1" style={{ lineHeight: 1.4 }}>
            We'll only text you about the games you play in (reminders, late RSVPs, urgent
            messages from your coach). Leave blank to skip — your email still works.
          </p>
        </form>
      </div>

      {/* ── App Theme ── */}
      <div className="card mb-5">
        <div className="flex items-center gap-2 mb-5">
          <Palette size={18} style={{ color: 'var(--ice)' }} />
          <h2 className="text-lg font-bold">App Theme</h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
          {[
            { id: 'midnight', label: 'Midnight Rink', desc: 'Premium dark navy',      bg: '#0c1222', surface: '#131b2e', accent: '#38bdf8', colors: ['#38bdf8','#34d399','#f87171','#fb923c'] },
            { id: 'arena',    label: 'Arena Blue',    desc: 'Bright sports dashboard', bg: '#f0f4f8', surface: '#ffffff', accent: '#1d4ed8', colors: ['#1d4ed8','#059669','#dc2626','#ea580c'] },
            { id: 'zen',      label: 'Zen Navy',      desc: 'Warm minimal calm',       bg: '#f6f4f0', surface: '#fefdfb', accent: '#2e4a7a', colors: ['#2e4a7a','#3d7a5f','#b83b3b','#c27832'] },
          ].map(t => {
            const isActive = theme === t.id;
            return (
              <button key={t.id} onClick={() => setTheme(t.id)} style={{
                display: 'flex', flexDirection: 'column', gap: 8,
                padding: 14, borderRadius: 12, cursor: 'pointer',
                background: isActive ? 'var(--ice-a8)' : 'var(--n-a3)',
                border: `2px solid ${isActive ? 'var(--ice)' : 'var(--border)'}`,
                textAlign: 'left', transition: 'all 0.2s',
              }}>
                <div style={{ width: '100%', height: 48, borderRadius: 8, overflow: 'hidden', display: 'flex', background: t.bg, border: '1px solid rgba(128,128,128,0.15)' }}>
                  <div style={{ width: '30%', background: t.surface, borderRight: '1px solid rgba(128,128,128,0.1)' }} />
                  <div style={{ flex: 1, padding: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <div style={{ height: 4, width: '60%', borderRadius: 2, background: t.accent }} />
                    <div style={{ height: 3, width: '80%', borderRadius: 2, background: t.accent, opacity: 0.2 }} />
                    <div style={{ height: 3, width: '40%', borderRadius: 2, background: t.accent, opacity: 0.1 }} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  {t.colors.map((c, i) => <div key={i} style={{ width: 12, height: 12, borderRadius: '50%', background: c, border: '1px solid rgba(128,128,128,0.15)' }} />)}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-display)', color: 'var(--text)' }}>
                    {t.label} {isActive && <Check size={13} style={{ display: 'inline', verticalAlign: '-2px', color: 'var(--ice)' }} />}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 1 }}>{t.desc}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Display Preferences ── */}
      <div className="card mb-5">
        <div className="flex items-center gap-2 mb-5">
          <Clock size={18} style={{ color: 'var(--ice)' }} />
          <h2 className="text-lg font-bold">Display Preferences</h2>
        </div>
        <label className="flex items-center justify-between py-2 cursor-pointer">
          <div>
            <div className="font-medium text-sm">Time Format</div>
            <div className="text-xs text-secondary mt-0.5">
              Preview: <span style={{ color: 'var(--ice)', fontFamily: 'var(--font-display)' }}>{formatTime('20:00')}</span>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0 ml-4">
            <span className="text-xs font-semibold" style={{ color: is24h ? 'var(--text-secondary)' : 'var(--ice)' }}>12h</span>
            <button type="button" onClick={toggleTimeFormat}
              className="relative w-11 h-6 rounded-full transition-all duration-200"
              style={{ background: is24h ? 'var(--ice)' : 'var(--border)' }}>
              <div className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all duration-200"
                style={{ left: is24h ? '22px' : '2px' }} />
            </button>
            <span className="text-xs font-semibold" style={{ color: is24h ? 'var(--ice)' : 'var(--text-secondary)' }}>24h</span>
          </div>
        </label>
      </div>

      {/* ── Notification prefs ── */}
      <div className="card mb-5">
        <div className="flex items-center gap-2 mb-5">
          <Bell size={18} style={{ color: 'var(--ice)' }} />
          <h2 className="text-lg font-bold">Notification Preferences</h2>
        </div>
        <div className="space-y-4 mb-5">
          {[
            { key: 'email',     label: 'Email Notifications', desc: 'Game reminders, player list updates, announcements' },
            { key: 'push',      label: 'Push Notifications',  desc: 'Real-time alerts on your device' },
            // v2.5.135 — SMS toggle. Only honored if the user also has
            // a verified phone with consent_at and an active Messaging
            // subscription; this toggle is the "even if all gates pass,
            // I don't want SMS right now" override.
            { key: 'sms',       label: 'SMS Notifications',   desc: 'Text messages for game reminders and announcements (requires verified phone + subscription)' },
            { key: 'reminders', label: 'Weekly Reminders',    desc: 'Automatic weekly game availability reminder' },
          ].map(item => (
            <label key={item.key} className="flex items-center justify-between py-2 cursor-pointer">
              <div>
                <div className="font-medium text-sm">{item.label}</div>
                <div className="text-xs text-secondary mt-0.5">{item.desc}</div>
              </div>
              <button type="button" onClick={() => setPrefs(p => ({ ...p, [item.key]: !p[item.key] }))}
                className="relative w-11 h-6 rounded-full transition-all duration-200 flex-shrink-0 ml-4"
                style={{ background: prefs[item.key] ? 'var(--ice)' : 'var(--border)' }}>
                <div className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all duration-200"
                  style={{ left: prefs[item.key] ? '22px' : '2px' }} />
              </button>
            </label>
          ))}
        </div>
        {push.supported && (
          <div className="pt-4 border-t border-white/8 mb-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <Smartphone size={15} style={{ color: 'var(--ice)' }} />
                <span>Browser Push</span>
                <span className={`badge ${push.enabled ? 'status-in' : 'status-pending'}`}>{push.enabled ? 'Active' : 'Inactive'}</span>
              </div>
              <button onClick={push.enabled ? disablePush : enablePush}
                className={push.enabled ? 'btn-ghost text-xs border border-white/10' : 'btn-ice text-xs'}>
                {push.enabled ? 'Disable' : 'Enable Push'}
              </button>
            </div>
            {/* Show re-enable hint if user previously said No Thanks */}
            {user?.notificationPrefs?.pushDismissed && !push.enabled && (
              <p className="text-xs mt-2" style={{ color: 'var(--text-2)' }}>
                You previously dismissed the notification prompt.{' '}
                <button
                  className="underline"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ice)', fontSize: 'inherit' }}
                  onClick={async () => {
                    try {
                      const updated = { ...(user?.notificationPrefs || {}), pushDismissed: false };
                      await api.put('/auth/profile', { notificationPrefs: updated });
                      updateUser({ notificationPrefs: updated });
                      localStorage.removeItem('push-prompt-shown');
                      toast.success('Notification prompt re-enabled — it will appear on your next visit.');
                    } catch { toast.error('Failed to update preference'); }
                  }}>
                  Tap here to re-enable
                </button>
              </p>
            )}
          </div>
        )}
        <button onClick={savePrefs} disabled={savingPrefs} className="btn-ice w-full justify-center">
          {savingPrefs ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <><Save size={15} /> Save Preferences</>}
        </button>
      </div>

      {/* ── My Stats Tiles (2026-07-25, mig 107) ──
          Per-user visibility toggles for every tile on the My Stats
          page + the dashboard My Stats card. Semantics: only explicit
          FALSE hides a tile; anything absent shows (new tiles default
          on). Saves immediately on toggle — no separate Save button —
          because each switch is an independent, low-stakes preference
          (mirrors how the pushDismissed inline action saves). */}
      <div className="card mb-5">
        <div className="flex items-center gap-2 mb-2">
          <BarChart2 size={18} style={{ color: 'var(--ice)' }} />
          <h2 className="text-lg font-bold">My Stats Tiles</h2>
        </div>
        <p className="text-xs text-secondary mb-4">
          Choose which stats show on your My Stats page and your dashboard stats card.
          {/* 2026-09-17 (per Gilles) — these toggles are also your privacy
              controls: a stat you switch OFF is hidden from your teammates
              and your coach on every leaderboard. */}
          {' '}<strong style={{ color: 'var(--text)' }}>Anything you switch off is also hidden from your teammates and coach</strong> — they'll see a 🔒 instead of the number.
        </p>
        <div className="space-y-1">
          {[
            { key: 'gamesPlayed',    label: 'Games Played' },
            { key: 'record',         label: 'My Record (W-L-T)' },
            { key: 'goals',          label: 'Goals For · Against' },
            { key: 'plusMinus',      label: 'Plus / Minus' },
            { key: 'attendance',     label: 'Attendance %' },
            { key: 'winRate',        label: 'Win Rate' },
            { key: 'streak',         label: 'Current Streak' },
            { key: 'rank',           label: 'Team Rank (#N of M)' },
            { key: 'rsvp',           label: 'Your Results (checked in, record, GF·GA, +/−)' },
            { key: 'achievements',   label: 'Achievements' },
            { key: 'winPerformance', label: 'Win Streaks' },
          ].map(item => {
            const on = user?.statsTilePrefs?.[item.key] !== false;
            return (
              <label key={item.key} className="flex items-center justify-between py-1.5 cursor-pointer">
                <div className="font-medium text-sm">{item.label}</div>
                <button type="button"
                  onClick={async () => {
                    const updated = { ...(user?.statsTilePrefs || {}), [item.key]: !on };
                    // Optimistic: flip locally first so the switch feels
                    // instant; revert via updateUser on failure.
                    const prev = user?.statsTilePrefs || {};
                    updateUser({ statsTilePrefs: updated });
                    try {
                      await api.put('/auth/profile', { statsTilePrefs: updated });
                    } catch {
                      updateUser({ statsTilePrefs: prev });
                      toast.error('Failed to save tile preference');
                    }
                  }}
                  className="relative w-11 h-6 rounded-full transition-all duration-200 flex-shrink-0 ml-4"
                  style={{ background: on ? 'var(--ice)' : 'var(--border)' }}>
                  <div className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all duration-200"
                    style={{ left: on ? '22px' : '2px' }} />
                </button>
              </label>
            );
          })}
        </div>
      </div>

      {/* ── Calendar Subscriptions (v2.5.97) ──
          Player-requested feature: subscribe to a team's schedule
          from Apple Calendar / Google Calendar / Outlook so games
          appear automatically. We mint a per-user, per-team token
          (24 random bytes → 32 base64url chars) that the calendar
          app fetches periodically. webcal:// is the iOS/macOS
          subscription scheme; https:// works in Google Calendar
          and Outlook ("From URL" → paste the link).

          Refresh frequency is determined by the calendar app itself
          (Apple ~hourly when "auto", Google ~daily). Our feed
          advertises REFRESH-INTERVAL: PT12H + X-PUBLISHED-TTL: PT12H
          per RFC 7986 / Apple's hint conventions. Some apps honor
          it, others ignore it — we tell the user 12-hour cadence
          is the typical refresh.

          One subscription per team — keeps the URL list short and
          gives users granular control (turn off Tuesday team
          calendar without affecting Sunday). */}
      {/* ── SMS messaging (v2.5.135 SMS-A) ──
          Phone number management + verification + opt-in. Lives
          adjacent to other notification settings. */}
      <UserSmsSection />

      {/* v2.5.147 — id anchor so SchedulePage's "Subscribe to
          calendar" link (/profile#calendar) scrolls here. */}
      <div id="calendar" className="card mb-5">
        <div className="flex items-center gap-2 mb-3">
          <Calendar size={18} style={{ color: 'var(--ice)' }} />
          <h2 className="text-lg font-bold">Calendar Subscriptions</h2>
        </div>
        <p className="text-secondary text-xs mb-4 -mt-1">
          Add your team's games to Apple Calendar, Google Calendar, or Outlook. Refreshes every ~12 hours.
        </p>

        {calendarLoading && (
          <div className="text-sm text-secondary py-2">Loading…</div>
        )}

        {!calendarLoading && calendarTokens.length === 0 && userTeams.length === 0 && (
          <p className="text-sm text-secondary py-2">
            You're not on any teams yet. Once you've joined a team, come back here to add a calendar subscription.
          </p>
        )}

        {!calendarLoading && calendarTokens.length > 0 && (
          <div className="space-y-3 mb-4">
            {calendarTokens.map(t => {
              const httpsUrl  = calendarFeedUrl(t.token, 'https');
              // 2026-07-07 FIX — back to plain webcal:// (was webcals://,
              // v2.5.185). Field report: iOS Safari rejected the webcals
              // link outright with "Safari cannot open the page because
              // the address is invalid" — the scheme isn't reliably
              // registered. webcal:// is universally handled, and the
              // plain-http first fetch it implies is upgraded by
              // Cloudflare's Always-Use-HTTPS 301 + the feed's HSTS
              // header (that pairing is the documented design — see
              // CLAUDE.md operator notes). Apple Calendar follows the
              // redirect silently; no "not secure" warning in practice.
              const webcalUrl = calendarFeedUrl(t.token, 'webcal');
              const lastAcc   = t.lastAccessed
                ? new Date(t.lastAccessed).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                : 'never';
              return (
                <div key={t.token} style={{
                  padding: 14, borderRadius: 12,
                  background: 'var(--surface-2)', border: '1px solid var(--border)',
                }}>
                  {/* Mobile fix: wrap + truncate so a long team name plus the
                      Regenerate/Revoke buttons don't force horizontal overflow
                      of the card on ~390px screens. */}
                  <div className="flex items-center justify-between mb-2" style={{ flexWrap: 'wrap', gap: 6 }}>
                    <div className="font-semibold text-sm" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: '1 1 auto' }}>{t.teamName}</div>
                    <div className="flex items-center gap-2" style={{ flexShrink: 0 }}>
                      {/* v2.5.148 — Regenerate button. Single click
                          (with confirm) replaces the token with a
                          fresh one. Saves the old 3-step revoke →
                          wait → recreate flow. */}
                      <button
                        type="button"
                        onClick={() => regenerateCalendarToken(t.token)}
                        disabled={regeneratingCalendar === t.token || revokingCalendar === t.token}
                        title="Generate a new URL (the current URL will stop working)"
                        style={{
                          appearance: 'none', background: 'transparent',
                          border: '1px solid var(--border)', borderRadius: 8,
                          color: 'var(--text-2)',
                          padding: '5px 9px', cursor: 'pointer',
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          fontSize: 11, fontWeight: 600,
                          opacity: (regeneratingCalendar === t.token) ? 0.5 : 1,
                        }}>
                        <RefreshCw size={12} />
                        {regeneratingCalendar === t.token ? 'Working…' : 'Regenerate'}
                      </button>
                      <button
                        type="button"
                        onClick={() => revokeCalendarToken(t.token)}
                        disabled={revokingCalendar === t.token || regeneratingCalendar === t.token}
                        title="Revoke this subscription"
                        style={{
                          appearance: 'none', background: 'transparent',
                          border: '1px solid rgba(255,68,68,0.30)', borderRadius: 8,
                          color: 'var(--penalty, #e34a4a)',
                          padding: '5px 9px', cursor: 'pointer',
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          fontSize: 11, fontWeight: 600,
                          opacity: (revokingCalendar === t.token) ? 0.5 : 1,
                        }}>
                        <Trash2 size={12} /> Revoke
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-secondary mb-2">
                    Last fetched: {lastAcc}{t.accessCount > 0 ? ` · ${t.accessCount} fetches` : ''}
                  </p>
                  <div className="flex items-center gap-2 mb-1">
                    <input
                      type="text"
                      readOnly
                      value={httpsUrl}
                      onClick={(e) => e.target.select()}
                      style={{
                        flex: 1, padding: '7px 10px',
                        background: 'var(--surface)', color: 'var(--text)',
                        border: '1px solid var(--border)', borderRadius: 8,
                        fontFamily: 'var(--font-mono, monospace)', fontSize: 11,
                        minWidth: 0,
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => copyFeedUrl(t.token)}
                      title="Copy URL"
                      style={{
                        appearance: 'none', background: copiedToken === t.token ? 'var(--goal)' : 'var(--surface)',
                        border: '1px solid var(--border)', borderRadius: 8,
                        color: copiedToken === t.token ? '#000' : 'var(--text)',
                        padding: '7px 10px', cursor: 'pointer',
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                      }}>
                      {copiedToken === t.token ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <a href={webcalUrl} className="btn-ghost text-xs" style={{ padding: '5px 10px' }}>
                      📱 Add to Apple Calendar
                    </a>
                    <a
                      href={`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(httpsUrl)}`}
                      target="_blank" rel="noopener noreferrer"
                      className="btn-ghost text-xs" style={{ padding: '5px 10px' }}>
                      📅 Add to Google Calendar
                    </a>
                    {/* v2.5.185 — Outlook subscribe. Outlook's web client
                        takes a feed via the addfromweb deep link with the
                        https ICS URL + a display name. Works for both
                        outlook.live.com (personal) and, for most users,
                        redirects appropriately if signed into a work
                        account. */}
                    <a
                      href={`https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(httpsUrl)}&name=${encodeURIComponent((t.teamName || 'Game Day') + ' Games')}`}
                      target="_blank" rel="noopener noreferrer"
                      className="btn-ghost text-xs" style={{ padding: '5px 10px' }}>
                      📆 Add to Outlook
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!calendarLoading && (() => {
          const tokenedTeamIds = new Set(calendarTokens.map(t => t.teamId));
          const eligible = userTeams.filter(team => !tokenedTeamIds.has(team.id));
          if (eligible.length === 0) return null;
          return (
            <div className="space-y-2 pt-2" style={{ borderTop: calendarTokens.length > 0 ? '1px solid var(--border)' : 'none' }}>
              <p className="text-xs text-secondary" style={{ marginTop: 10 }}>
                Add a subscription for:
              </p>
              {eligible.map(team => (
                <button
                  key={team.id}
                  type="button"
                  onClick={() => createCalendarToken(team.id)}
                  disabled={creatingCalendarFor === team.id}
                  className="w-full flex items-center justify-between"
                  style={{
                    padding: '10px 14px',
                    background: 'var(--surface-2)',
                    border: '1px solid var(--border)',
                    borderRadius: 10,
                    color: 'var(--text)',
                    cursor: 'pointer',
                  }}>
                  <span className="text-sm font-medium">{team.name}</span>
                  <span className="text-xs" style={{ color: 'var(--ice)' }}>
                    {creatingCalendarFor === team.id ? 'Creating…' : '+ Add'}
                  </span>
                </button>
              ))}
            </div>
          );
        })()}
      </div>

      {/* ── Payment Credits (Apr 2026) ──
          Moved to the dedicated Activity page (/activity). The nav
          item only appears for users with transaction history, so it
          stays out of the way until it's relevant. Credit balance,
          history, and refund-to-card now all live there alongside
          the payment history it was previously missing. */}

      {/* ── Change Email (Apr 2026, Tom_Notes H1) ──
          Self-serve email change with verify-by-link.
          Two states:
            (a) idle: input + "Send confirmation link" button
            (b) request-sent: explainer + "Cancel" button
          The actual email swap happens server-side when the user
          clicks the link in their new inbox; this UI just kicks off
          the flow and tracks request-in-flight state. */}
      <div className="card mb-5">
        <div className="flex items-center gap-2 mb-3">
          <Mail size={18} style={{ color: 'var(--ice)' }} />
          <h2 className="text-lg font-bold">Change Email</h2>
        </div>
        <p className="text-secondary text-xs mb-4 -mt-1">
          We'll send a confirmation link to your new address. The change happens
          when you click it — your current email keeps working until then.
        </p>
        {emailRequestSent ? (
          <div className="p-4 rounded-xl mb-2"
            style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.2)' }}>
            <p className="text-sm font-semibold mb-1">📬 Check your new inbox</p>
            <p className="text-xs text-secondary mb-3" style={{ lineHeight: 1.5 }}>
              We sent a confirmation link to <strong>{emailDraft}</strong>. Click
              the link there to complete the change. The link expires in 24 hours.
            </p>
            <button type="button" onClick={cancelEmailChange} disabled={savingEmail}
              className="btn-ghost text-xs px-3 py-1.5">
              {savingEmail ? '…' : 'Cancel pending change'}
            </button>
          </div>
        ) : (
          <form onSubmit={requestEmailChange} className="space-y-3">
            <div>
              <label className="label">Current email</label>
              <input type="email" className="input-field" value={user?.email || ''} disabled
                style={{ opacity: 0.7 }} />
            </div>
            <div>
              <label className="label">New email</label>
              <input type="email" className="input-field"
                placeholder="you@newdomain.com"
                autoComplete="off"
                value={emailDraft}
                onChange={e => setEmailDraft(e.target.value)} />
            </div>
            <button type="submit" disabled={savingEmail || !emailDraft.trim()}
              className="btn-ice flex items-center gap-2">
              {savingEmail
                ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Sending…</>
                : <><Mail size={14} /> Send confirmation link</>}
            </button>
          </form>
        )}
      </div>

      {/* ── Security ── */}
      <div className="card mb-5">
        <div className="flex items-center gap-2 mb-5">
          <Shield size={18} style={{ color: 'var(--ice)' }} />
          <h2 className="text-lg font-bold">Change Password</h2>
        </div>
        <p className="text-secondary text-xs mb-4 -mt-2">
          Min 10 characters; must include uppercase, lowercase, a number,
          and a special character. Changing your password signs out other devices.
        </p>
        <form onSubmit={changePwd} className="space-y-3">
          <div>
            <label className="label">Current Password</label>
            <div className="relative">
              <input type={showPwds ? 'text' : 'password'} required className="input-field pr-10" placeholder="••••••••"
                value={pwdForm.current} onChange={e => setPwdForm(p => ({ ...p, current: e.target.value }))}
                autoComplete="current-password" />
              <button type="button" onClick={() => setShowPwds(v => !v)}
                className="absolute right-3 top-3.5 text-secondary hover:text-primary"
                title={showPwds ? 'Hide passwords' : 'Show passwords'}>
                {showPwds ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>
          <div>
            <label className="label">New Password</label>
            <input type={showPwds ? 'text' : 'password'} required className="input-field"
              placeholder="At least 10 characters"
              value={pwdForm.newPwd} onChange={e => setPwdForm(p => ({ ...p, newPwd: e.target.value }))}
              autoComplete="new-password" />
            {/* Live requirement checklist — matches the register + reset flows
                so the password rules look identical everywhere. Only shown
                once the user starts typing to avoid clutter on first view. */}
            {pwdForm.newPwd && (() => {
              const p = pwdForm.newPwd;
              const checks = [
                { ok: p.length >= 10,         label: 'At least 10 characters' },
                { ok: /[A-Z]/.test(p),        label: 'One uppercase letter (A–Z)' },
                { ok: /[a-z]/.test(p),        label: 'One lowercase letter (a–z)' },
                { ok: /[0-9]/.test(p),        label: 'One number (0–9)' },
                { ok: /[^A-Za-z0-9]/.test(p), label: 'One special character (e.g. !@#$%)' },
              ];
              return (
                <ul style={{ marginTop: 8, paddingLeft: 0, listStyle: 'none' }}>
                  {checks.map(c => (
                    <li key={c.label}
                      style={{
                        fontSize: 12, lineHeight: 1.6,
                        color: c.ok ? 'var(--goal)' : 'var(--text-2)',
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}>
                      <span style={{
                        display: 'inline-block', width: 14, textAlign: 'center',
                        color: c.ok ? 'var(--goal)' : 'var(--text-3)', fontWeight: 700,
                      }}>
                        {c.ok ? '✓' : '○'}
                      </span>
                      {c.label}
                    </li>
                  ))}
                </ul>
              );
            })()}
          </div>
          <div>
            <label className="label">Confirm New Password</label>
            <input type={showPwds ? 'text' : 'password'} required className="input-field" placeholder="••••••••"
              value={pwdForm.confirm} onChange={e => setPwdForm(p => ({ ...p, confirm: e.target.value }))}
              autoComplete="new-password" />
            {pwdForm.confirm && pwdForm.confirm !== pwdForm.newPwd && (
              <p className="text-xs mt-1" style={{ color: 'var(--penalty)' }}>Passwords don't match</p>
            )}
          </div>
          <button type="submit" disabled={savingPwd || !pwdForm.current} className="btn-ghost border border-white/15 w-full justify-center mt-1">
            {savingPwd ? <div className="spinner" style={{ width: 16, height: 16 }} /> : 'Update Password'}
          </button>
        </form>
      </div>

      {/* ── Personal Stats ── */}
      {myStats && (
        <div className="glass rounded-xl p-4 mb-5">
          <div className="flex items-center gap-2 mb-4 text-sm font-semibold text-secondary uppercase tracking-wide">
            <Shield size={14} /> Your Stats
          </div>
          <div className="grid grid-cols-4 gap-2 mb-4">
            {[
              { label: 'Teams',    value: myStats.teams?.length ?? 0, color: 'var(--ice)'     },
              { label: 'Games In', value: myStats.gamesIn,            color: 'var(--goal)'    },
              { label: 'Wins',     value: myStats.wins,               color: 'var(--goal)'    },
              { label: 'Losses',   value: myStats.losses,             color: 'var(--penalty)' },
            ].map(s => (
              <div key={s.label} className="rounded-lg p-2.5 text-center"
                style={{ background: `${s.color}08`, border: `1px solid ${s.color}18` }}>
                <div className="text-2xl font-bold" style={{ color: s.color, fontFamily: 'var(--font-display)' }}>{s.value}</div>
                <div className="text-xs text-secondary mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
          {myStats.teams?.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs text-secondary mb-2 uppercase tracking-wide font-semibold">My Teams</p>
              {myStats.teams.map(t => (
                <div key={t.id} className="flex items-center justify-between py-1.5 text-sm gap-2">
                  <span className="truncate font-medium flex-1 min-w-0">{t.name}</span>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {!t.is_primary && <span className="badge text-xs" style={{ background: 'rgba(255,149,0,0.1)', color: 'var(--puck)' }}>spare</span>}
                    <span className="badge text-xs" style={{ background: 'var(--ice-a8)', color: 'var(--ice)' }}>{t.member_role}</span>
                    {/* May 4 2026 (v2.5.70) — Leave button per team. Coach
                        feedback hid the Team Settings nav for non-coaches,
                        which removed their only path to the Danger sub-page
                        where Leave Team used to live. Surfacing it here
                        keeps the feature reachable. Coaches see it too —
                        symmetric is simpler than role-gating just this row.
                        Backend POST /teams/:id/leave already enforces the
                        sole-coach guard, so a coach who's the last coach
                        gets a friendly toast error instead of bricking
                        the team. ── */}
                    <button
                      type="button"
                      onClick={() => setLeaveTarget({ id: t.id, name: t.name })}
                      title={`Leave ${t.name}`}
                      aria-label={`Leave team ${t.name}`}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        padding: '3px 8px', borderRadius: 6,
                        background: 'rgba(255,68,68,0.06)',
                        border: '1px solid rgba(255,68,68,0.20)',
                        color: 'var(--penalty)',
                        fontSize: 11, fontWeight: 700, cursor: 'pointer',
                        WebkitTapHighlightColor: 'transparent',
                      }}>
                      <LogOut size={11} /> Leave
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {myStats.gamesPlayed > 0 && (
            <p className="text-xs text-secondary mt-3 pt-3 border-t border-white/5">
              {myStats.gamesPlayed} game{myStats.gamesPlayed !== 1 ? 's' : ''} played
              {myStats.wins + myStats.losses > 0 && ` · ${myStats.wins}W ${myStats.losses}L${myStats.ties > 0 ? ` ${myStats.ties}T` : ''}`}
            </p>
          )}
        </div>
      )}

      {/* ── Achievements ── */}
      {achievements && achievements.badges?.length > 0 && (
        <div className="zen-card p-5 animate-fade-in mb-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-base flex items-center gap-2">🏅 Achievements</h2>
            <div className="text-xs font-bold" style={{ color: 'var(--zen)', fontFamily: 'var(--font-display)', letterSpacing: '0.1em' }}>
              {achievements.badges.length} UNLOCKED
            </div>
          </div>
          {achievements.stats.currentStreak > 0 && (
            <div className="mb-4 p-3 rounded-xl flex items-center gap-3"
              style={{ background: 'rgba(255,107,53,0.08)', border: '1px solid rgba(255,107,53,0.15)' }}>
              <div className="text-2xl animate-breathe">🔥</div>
              <div>
                <div className="font-bold text-sm" style={{ color: 'var(--fire)' }}>{achievements.stats.currentStreak}-Game Streak</div>
                <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>Best: {achievements.stats.maxStreak} · Attendance: {achievements.stats.attendancePct}%</div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {achievements.badges.map(b => (
              <div key={b.id} className={`achievement unlocked ${b.tier === 'gold' ? 'gold' : ''}`} title={b.desc}>
                <div className="text-3xl mb-1">{b.emoji}</div>
                <div className="text-xs font-bold" style={{ color: b.tier === 'gold' ? 'var(--puck)' : 'var(--ice)', fontFamily: 'var(--font-display)', lineHeight: 1.2 }}>{b.label}</div>
                <div className="text-xs mt-0.5 line-clamp-2" style={{ color: 'var(--text-muted)', fontSize: 9 }}>{b.desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Account Info ── */}
      <div className="glass rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-secondary uppercase tracking-wide">
          <Mail size={14} /> Account Info
        </div>
        <div className="text-xs text-secondary space-y-1.5">
          <p>Email: <span className="text-primary">{user?.email}</span></p>
          <p>Member since: <span className="text-primary">{user?.createdAt ? new Date(user.createdAt).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' }) : '—'}</span></p>
          <p className="pt-2 opacity-60">All personal data is encrypted at rest (AES-256). Game Day never sells your data.</p>
        </div>

        {/* Replay onboarding tour */}
        <button
          onClick={async () => {
            try {
              await api.put('/auth/profile', { onboardingCompleted: false });
              updateUser({ onboardingCompleted: false });
              toast.success('The Getting Started tour will appear in a moment.');
            } catch { toast.error('Failed to reset tour'); }
          }}
          className="mt-4 w-full text-xs py-2 rounded-lg btn-ghost border border-white/10"
          style={{ color: 'var(--text-2)' }}>
          🎓 Replay Getting Started Tour
        </button>
      </div>

      {/* ── Danger Zone ──
          Deliberately styled with red borders + warning copy so the user
          can't miss what they're about to do. The Delete Account button
          opens a confirmation modal that requires them to type "DELETE"
          AND (for password accounts) re-enter their password before the
          API call fires. Account deletion is irreversible. */}
      <div className="rounded-xl p-4"
        style={{
          background: 'rgba(255,68,68,0.04)',
          border: '1px solid rgba(255,68,68,0.25)',
        }}>
        <div className="flex items-center gap-2 mb-2 text-sm font-semibold uppercase tracking-wide"
          style={{ color: 'var(--penalty)' }}>
          ⚠️ Danger Zone
        </div>
        <p className="text-xs mb-3" style={{ color: 'var(--text-2)', lineHeight: 1.5 }}>
          Deleting your account is permanent and cannot be undone. Your RSVPs,
          chat messages, stats, and notification preferences will all be removed.
          Your coaches will see a "(account deleted)" badge in their player list so
          they know your spot is open.
        </p>
        <button
          onClick={() => {
            setDeleteConfirmText('');
            setDeletePassword('');
            setDeleteAcknowledged(false);
            setShowDeleteModal(true);
          }}
          className="w-full text-sm py-2.5 rounded-lg font-semibold"
          style={{
            background: 'rgba(255,68,68,0.10)',
            border: '1.5px solid rgba(255,68,68,0.40)',
            color: 'var(--penalty)',
          }}>
          🗑 Delete My Account
        </button>
      </div>

      {/* ── Delete account confirmation modal ──
          Triple-confirmation: explicit acknowledgment checkbox, typed
          "DELETE" string, and (for password accounts) password re-entry.
          Each requirement is independently necessary — you can't get to
          the final button without all three. */}
      {showDeleteModal && (
        <div
          onClick={() => !deleting && setShowDeleteModal(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1100,
            background: 'rgba(0,0,0,0.80)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16, animation: 'fadeIn 0.2s ease',
            // Allow the outer flex container to scroll if the modal is
            // taller than the viewport — without this, the bottom buttons
            // get clipped on landscape phones / short viewports and the
            // user is stuck unable to confirm or cancel.
            overflowY: 'auto',
          }}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 460,
              // Mobile fix: this modal holds text inputs (DELETE confirm +
              // password) that trigger the native keyboard. --mobile-h is
              // set by useCapacitorPlugins to the keyboard-aware viewport
              // height in the Capacitor wrapper; bare 100dvh doesn't shrink
              // for the software keyboard there, which could push the
              // Cancel/Delete buttons off-screen while typing.
              maxHeight: 'calc(var(--mobile-h, 100dvh) - 32px)',
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 16,
              padding: 24,
              boxShadow: '0 20px 60px rgba(0,0,0,0.7)',
              // Inner-scroll fallback for very tall modal content on
              // short viewports. Combined with maxHeight, this means the
              // body scrolls inside the modal frame instead of pushing
              // buttons off-screen.
              overflowY: 'auto',
            }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span style={{ fontSize: 28 }}>⚠️</span>
              <h3 style={{ fontSize: 18, fontWeight: 800, color: 'var(--penalty)', margin: 0 }}>
                Delete Account?
              </h3>
            </div>
            <p style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.5, marginBottom: 14 }}>
              This will permanently delete your Game Day account. The following
              data will be erased and <strong>cannot be recovered</strong>:
            </p>
            <ul style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.7, marginBottom: 14, paddingLeft: 18 }}>
              <li>Your profile, name, email, avatar, and notification preferences</li>
              <li>All RSVPs and game responses you've ever submitted</li>
              <li>All chat messages you've sent</li>
              <li>Your personal stats, badges, and attendance history</li>
              <li>Your saved push notification subscriptions</li>
            </ul>
            <p style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5, marginBottom: 14 }}>
              Coaches and team player lists will continue to show your name with an
              <strong> (account deleted)</strong> tag so they know the spot is
              vacant. They cannot recover your data either.
            </p>

            {/* Acknowledgment checkbox — first gate */}
            <label
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: 10, borderRadius: 8,
                background: deleteAcknowledged ? 'rgba(255,68,68,0.08)' : 'var(--surface-2)',
                cursor: 'pointer', marginBottom: 12,
              }}>
              <input
                type="checkbox"
                checked={deleteAcknowledged}
                onChange={(e) => setDeleteAcknowledged(e.target.checked)}
                style={{ marginTop: 2, accentColor: 'var(--penalty)', flexShrink: 0 }}
              />
              <span style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.4 }}>
                I understand this is permanent and my data cannot be recovered.
              </span>
            </label>

            {/* Type-DELETE confirmation — second gate */}
            <label style={{ display: 'block', marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--text-2)', marginBottom: 4 }}>
                Type <strong style={{ color: 'var(--penalty)', fontFamily: 'monospace' }}>DELETE</strong> to confirm:
              </div>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                autoComplete="off"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: 16,  // iOS Safari auto-zooms inputs <16px on focus
                  fontFamily: 'monospace',
                  background: 'var(--surface-2)',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  color: 'var(--text)',
                }}
              />
            </label>

            {/* Password — third gate (skip for OAuth-only accounts) */}
            <label style={{ display: 'block', marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: 'var(--text-2)', marginBottom: 4 }}>
                Confirm your password:
              </div>
              <input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Your password"
                autoComplete="current-password"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: 16,  // iOS Safari auto-zooms inputs <16px on focus
                  background: 'var(--surface-2)',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  color: 'var(--text)',
                }}
              />
              <p style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 4 }}>
                If you signed in with Google or Apple, leave this blank.
              </p>
            </label>

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="btn-ghost flex-1 py-2.5 text-sm">
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (deleting) return;
                  setDeleting(true);
                  try {
                    await api.delete('/auth/me', {
                      data: {
                        confirm: 'DELETE',
                        password: deletePassword || undefined,
                      },
                    });
                    toast.success('Account deleted. Goodbye 👋');
                    // Sign the user out + clear local state. The logout
                    // call to the server may 401 (account already gone)
                    // but the client-side cleanup still runs.
                    try { await logout(); } catch {}
                    // Hard redirect so any cached app state is cleared.
                    window.location.href = '/';
                  } catch (err) {
                    const msg = err?.response?.data?.error || 'Failed to delete account';
                    toast.error(msg);
                    setDeleting(false);
                  }
                }}
                disabled={
                  deleting ||
                  !deleteAcknowledged ||
                  deleteConfirmText !== 'DELETE'
                }
                className="flex-1 py-2.5 text-sm font-semibold rounded-lg"
                style={{
                  background: (deleting || !deleteAcknowledged || deleteConfirmText !== 'DELETE')
                    ? 'var(--surface-3)'
                    : 'var(--penalty)',
                  color: (deleting || !deleteAcknowledged || deleteConfirmText !== 'DELETE')
                    ? 'var(--text-3)'
                    : '#fff',
                  border: 'none',
                  cursor: (deleting || !deleteAcknowledged || deleteConfirmText !== 'DELETE')
                    ? 'not-allowed' : 'pointer',
                }}>
                {deleting ? 'Deleting…' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Leave Team confirm modal (May 4 2026 / v2.5.70) ──
          Lifted from TeamPage's Danger sub-page since the Team
          Settings nav entry is no longer reachable for non-coach
          players. Single confirmation step (no two-step like Delete
          Team — leaving is reversible by re-invite, so the higher
          friction isn't justified). The backend POST /teams/:id/leave
          enforces the sole-coach guard separately and surfaces
          its own friendly error. ── */}
      {leaveTarget && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4"
          style={{ zIndex: 500 }}
          onClick={e => { if (e.target === e.currentTarget && !leaving) setLeaveTarget(null); }}>
          <div className="rounded-2xl max-w-sm w-full p-6"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <div className="flex items-center gap-3 mb-3">
              <div style={{
                width: 36, height: 36, borderRadius: '50%',
                background: 'rgba(255,68,68,0.10)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <LogOut size={18} style={{ color: 'var(--penalty)' }} />
              </div>
              <h3 className="font-bold text-base">Leave {leaveTarget.name}?</h3>
            </div>
            <p className="text-sm text-secondary mb-5" style={{ lineHeight: 1.5 }}>
              You'll be removed from the player list. Your past attendance
              and stats stay with the team but you won't see future games
              until a coach re-invites you.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => !leaving && setLeaveTarget(null)}
                disabled={leaving}
                className="flex-1 py-2.5 text-sm font-semibold rounded-lg"
                style={{
                  background: 'var(--surface-2)', border: '1px solid var(--border)',
                  color: 'var(--text)', cursor: leaving ? 'not-allowed' : 'pointer',
                }}>
                Cancel
              </button>
              <button
                type="button"
                disabled={leaving}
                onClick={async () => {
                  setLeaving(true);
                  try {
                    await api.post(`/teams/${leaveTarget.id}/leave`);
                    toast.success(`Left ${leaveTarget.name}`);
                    setLeaveTarget(null);
                    // Refresh the local team list. /auth/me/stats includes
                    // teams, same payload we loaded on mount.
                    api.get('/auth/me/stats').then(r => setMyStats(r.data)).catch(() => {});
                  } catch (err) {
                    toast.error(err.response?.data?.error || 'Failed to leave team');
                  }
                  setLeaving(false);
                }}
                className="flex-1 py-2.5 text-sm font-semibold rounded-lg"
                style={{
                  background: leaving ? 'var(--surface-3)' : 'var(--penalty)',
                  color: leaving ? 'var(--text-3)' : '#fff',
                  border: 'none',
                  cursor: leaving ? 'not-allowed' : 'pointer',
                }}>
                {leaving ? 'Leaving…' : 'Leave Team'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
